import React, { useMemo, useRef, useState } from "react";

const buildSignature = (file) => `${file.name}::${file.size}::${file.lastModified}`;

const extractClipboardImageFiles = (clipboardData) => {
  if (!clipboardData) return [];

  const fromFiles = Array.from(clipboardData.files || []).filter((file) =>
    String(file?.type || "").startsWith("image/"),
  );

  const fromItems = Array.from(clipboardData.items || [])
    .filter(
      (item) =>
        item?.kind === "file" && String(item?.type || "").startsWith("image/"),
    )
    .map((item) => item.getAsFile?.())
    .filter(Boolean);

  return [...fromFiles, ...fromItems];
};

export default function ImageUploadModal({
  open,
  title,
  subtitle,
  files,
  onFilesChange,
  onClose,
  multiple = true,
  accept = "image/*",
  maxFiles = 30,
  primaryActionLabel = "",
  onPrimaryAction,
  primaryActionDisabled = false,
}) {
  const inputRef = useRef(null);
  const pastePadRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isClipboardLoading, setIsClipboardLoading] = useState(false);

  const normalizedFiles = useMemo(() => Array.from(files || []), [files]);

  const mergeIncoming = (incoming) => {
    const nextFiles = Array.from(incoming || []).filter((file) =>
      String(file?.type || "").startsWith("image/"),
    );

    const existing = multiple ? normalizedFiles : [];
    const signatures = new Set(existing.map((f) => buildSignature(f)));
    const merged = [...existing];

    for (const file of nextFiles) {
      const sig = buildSignature(file);
      if (signatures.has(sig)) continue;
      signatures.add(sig);
      merged.push(file);
      if (merged.length >= maxFiles) break;
      if (!multiple) break;
    }

    onFilesChange(multiple ? merged : merged.slice(-1));
  };

  const handlePasteEvent = (e) => {
    const pasted = extractClipboardImageFiles(e.clipboardData);
    if (!pasted.length) return;
    e.preventDefault();
    mergeIncoming(pasted);
  };

  const handlePasteFromClipboard = async () => {
    if (navigator?.clipboard && typeof navigator.clipboard.read === "function") {
      setIsClipboardLoading(true);
      try {
        const clipboardItems = await navigator.clipboard.read();
        const imageFiles = [];

        for (const item of clipboardItems) {
          const imageType = (item.types || []).find((t) =>
            String(t).startsWith("image/"),
          );
          if (!imageType) continue;
          const blob = await item.getType(imageType);
          const extension = imageType.includes("png")
            ? "png"
            : imageType.includes("jpeg")
              ? "jpg"
              : imageType.split("/")[1] || "img";
          const file = new File([blob], `clipboard-${Date.now()}.${extension}`, {
            type: imageType,
          });
          imageFiles.push(file);
          if (!multiple) break;
        }

        if (imageFiles.length) {
          mergeIncoming(imageFiles);
          return;
        }
      } catch (error) {
        console.error("Clipboard read failed:", error);
      } finally {
        setIsClipboardLoading(false);
      }
    }

    pastePadRef.current?.focus();
    window.alert(
      "Direct clipboard read isn’t available here. Tap the Paste Area, then use iPhone Paste from the context menu.",
    );
  };

  if (!open) return null;

  return (
    <div
      className="events-upload-modal-backdrop"
      onClick={() => {
        setIsDragActive(false);
        onClose?.();
      }}
    >
      <div
        className="events-upload-modal"
        onClick={(e) => e.stopPropagation()}
        onPaste={handlePasteEvent}
      >
        <div className="events-upload-modal-header">
          <h4>{title || "Upload images"}</h4>
          <button
            className="events-create-modal-close"
            onClick={() => {
              setIsDragActive(false);
              onClose?.();
            }}
          >
            ✕
          </button>
        </div>

        {subtitle ? <p className="events-upload-subtitle">{subtitle}</p> : null}

        <div
          className={`events-upload-dropzone ${isDragActive ? "active" : ""}`}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragActive(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragActive(false);
            mergeIncoming(e.dataTransfer?.files || []);
          }}
          tabIndex={0}
        >
          <p>Drag & drop image files here</p>
          <p>or paste from clipboard (Ctrl/Cmd+V)</p>
          <div className="events-upload-actions-inline">
            <button onClick={() => inputRef.current?.click()}>Browse files</button>
            <button
              type="button"
              onClick={handlePasteFromClipboard}
              disabled={isClipboardLoading}
              title="Tap after copying an image"
            >
              {isClipboardLoading ? "Reading..." : "Paste image"}
            </button>
          </div>

          <div
            ref={pastePadRef}
            className="events-upload-paste-pad"
            contentEditable
            suppressContentEditableWarning
            onPaste={handlePasteEvent}
            onInput={(e) => {
              e.currentTarget.textContent = "";
            }}
            role="textbox"
            aria-label="Paste area"
          >
            Mobile fallback: tap here, then long-press and choose Paste.
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            style={{ display: "none" }}
            onChange={(event) => {
              mergeIncoming(event.target.files || []);
              event.target.value = "";
            }}
          />
        </div>

        {!!normalizedFiles.length && (
          <div className="events-upload-file-list">
            {normalizedFiles.map((file, idx) => (
              <div key={buildSignature(file)} className="events-upload-file-item">
                <span>{file.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    const next = normalizedFiles.filter((_, i) => i !== idx);
                    onFilesChange(next);
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="events-upload-modal-footer">
          <button
            type="button"
            className="events-btn events-btn-secondary"
            onClick={() => {
              setIsDragActive(false);
              onClose?.();
            }}
          >
            Done
          </button>
          {onPrimaryAction && primaryActionLabel ? (
            <button
              type="button"
              className="events-btn events-btn-primary"
              onClick={() => onPrimaryAction?.()}
              disabled={primaryActionDisabled}
            >
              {primaryActionLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
